import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  InputLabel,
  Box,
  Typography,
} from "@mui/material";
import { Close, CloudUpload, Delete } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  SelectInputComponent,
  TextInputComponent,
} from "../../common/components";
import "./add-guide.scss";
import { createGuide } from "../../api/guides";
import { TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
interface AddGuidePopupProps {
  open: boolean;
  mediaType: "Video" | "Text";
  onClose: () => void;
  setIsLoading: (loading: boolean) => void;
  categories: string[];
  setAlertMessage: (message: string) => void;
  setAlertOpen: (open: boolean) => void;
  setAlertType: (type: "success" | "error") => void;
  setSelectedCategory?: (category: string) => void;
  setIndexValue?: (index: number) => void;
  setCurrentPage?: (page: number) => void;
  currentPage?: number;
  selectedCategory?: string;
  tabIndexValue?: number;
  fetchGuides?: () => void;
}

const AddGuidePopup: React.FC<AddGuidePopupProps> = ({
  open,
  mediaType,
  onClose,
  setIsLoading,
  categories,
  setAlertMessage,
  setAlertOpen,
  setAlertType,
  setSelectedCategory,
  setIndexValue,
  setCurrentPage,
  currentPage,
  selectedCategory,
  tabIndexValue,
  fetchGuides,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [thumbnailError, setThumbnailError] = useState<string | null>(null);

  const initialValues = {
    mediaType: mediaType,
    title: "",
    category: "",
  };
  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    category: Yup.string().required("Category is required"),
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size for text media type (50MB limit)
      const fileSizeInMB = file.size / (1024 * 1024);
      const maxFileSize =
        mediaType === "Text" ? 15 : mediaType === "Video" ? 100 : 0;
      if (fileSizeInMB > maxFileSize) {
        setFileError(`File size exceeds ${maxFileSize}MB limit`);
        return;
      }

      if (mediaType === "Video") {
        const allowedVideoTypes = ["video/mp4"];
        if (!allowedVideoTypes.includes(file.type)) {
          setFileError("Video format must be MP4");
          return;
        }
      }

      if (mediaType === "Text") {
        const allowedTextTypes = [
          "application/pdf",
          // "application/msword",
          // "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          // "application/vnd.ms-powerpoint",
          // "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        ];
        if (!allowedTextTypes.includes(file.type)) {
          setFileError(
            "Invalid file format. Allowed: pdf"
          );
          return;
        }
      }

      setFileError(null);
      setSelectedFile(file);
      if (mediaType === "Video") {
        const videoUrl = URL.createObjectURL(file);
        setFilePreview(videoUrl);
      } else {
        setFilePreview(file.name);
      }
    }
  };

  const handleThumbnailUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size for thumbnail (5MB limit)
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > 5) {
        setThumbnailError(`Thumbnail size exceeds 5MB limit`);
        return;
      }

      const allowedImageTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "image/heif",
        "image/webp",
      ];
      if (!allowedImageTypes.includes(file.type)) {
        setThumbnailError(
          "Invalid image format. Allowed: png, jpg, jpeg, heif, webp"
        );
        return;
      }

      setThumbnailError(null);
      setSelectedThumbnail(file);
      const imageUrl = URL.createObjectURL(file);
      setThumbnailPreview(imageUrl);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    setFileError(null);
  };

  const removeThumbnail = () => {
    setSelectedThumbnail(null);
    setThumbnailPreview(null);
    setThumbnailError(null);
  };

  const handleSubmit = async (values: {
    title: string;
    category: string;
    mediaType: string;
  }) => {
    try {
      if (!selectedFile) {
        setAlertMessage("Please upload a file");
        setAlertType("error");
        setAlertOpen(true);
        return;
      }
      setIsLoading(true);
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("mediaType", values.mediaType);
      formData.append("category", values.category);
      formData.append("file", selectedFile);
      if (selectedThumbnail && mediaType === "Video") {
        formData.append("thumbnail", selectedThumbnail);
      }
      const response = await createGuide(formData);
      if (response?.status === 200 || response?.status === 201) {
        const tabIndex = mediaType === "Text" ? 0 : 1;

        if (
          tabIndexValue === tabIndex &&
          currentPage === 1 &&
          selectedCategory === values.category
        ) {
          fetchGuides?.();
        } else {
          if (setIndexValue) {
            setIndexValue(tabIndex);
          }
          if (setCurrentPage) {
            setCurrentPage(1);
          }
          if (setSelectedCategory) {
            setSelectedCategory(values.category);
          }
        }
        setAlertMessage(`${mediaType} Guide added successfully!`);
        setAlertType("success");
        setAlertOpen(true);
        onClose();
      }
    } catch (error: any) {
      console.log(error);
      setAlertMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to create guide. Please try again."
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form state when popup is opened to clear previous data
  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setSelectedThumbnail(null);
      setFilePreview(null);
      setThumbnailPreview(null);
      setFileError(null);
      setThumbnailError(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      className="add-guide-popup"
    >
      <DialogTitle>
        Add {mediaType} Guide
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className="add-guide-form">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, isValid }) => {
            const isFormValid =
              isValid &&
              selectedFile &&
              !fileError &&
              !thumbnailError &&
              values.title.trim() !== "" &&
              values.category.trim() !== "";

            return (
              <Form>
                <div className="form-fields">
                  <TextInputComponent
                    type="text"
                    fieldName="title"
                    label="Title"
                    placeholder="Enter Title"
                    optional={true}
                    isRequired={true}
                    disabled={false}
                    value={values.title}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue("title", e?.target?.value)
                    }
                    maxlength="50"
                  />
                  <div className="form-select-input-component required">
                    <InputLabel
                      required={true}
                      className="select-label"
                      htmlFor="category-select"
                    >
                      Category
                    </InputLabel>
                    <Autocomplete
                      disablePortal={true}
                      options={categories?.map((cat) => ({
                        label: cat,
                        value: cat,
                      }))}
                      getOptionLabel={(option) => option.label}
                      onChange={(_event, selectedField) => {
                        setFieldValue("category", selectedField?.label || "");
                      }}
                      value={
                        values.category
                          ? { label: values.category, value: values.category }
                          : null
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.value === value?.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Category"
                          variant="outlined"
                          InputLabelProps={{ shrink: false }}
                          sx={{ fontSize: "1rem !important" }}
                        />
                      )}
                      slotProps={{
                        popper: {
                          sx: { zIndex: 1 },
                        },
                      }}
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          paddingRight: 0,
                          paddingLeft: "9px",
                          padding: 0,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#7D7D7D", // ✅ Default border color
                          },
                        },
                        "& .MuiAutocomplete-input": {
                          padding: "7.5px 5px 7.5px 10px !important",
                          fontSize: "1rem !important",
                        },
                      }}
                    />
                  </div>
                </div>
                <div
                  className={`file-upload-section ${mediaType === "Video" ? "video-section" : ""}`}
                >
                  <div className="file-upload-group">
                    <InputLabel required={true} className="file-label">
                      Upload {mediaType} File
                    </InputLabel>
                    {!selectedFile ? (
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        className="upload-button"
                      >
                        Choose {mediaType} File
                        <input
                          type="file"
                          hidden
                          accept={
                            mediaType === "Video"
                              ? "video/mp4"
                              : ".pdf"
                          }
                          onChange={handleFileUpload}
                        />
                      </Button>
                    ) : (
                      <Box className="file-preview">
                        {mediaType === "Video" && filePreview ? (
                          <Box className="video-preview">
                            <video width="180" height="100" controls>
                              <source src={filePreview} />
                            </video>
                            <Box className="file-info">
                              <Typography variant="body2">
                                {selectedFile.name}
                              </Typography>
                              <IconButton
                                onClick={removeFile}
                                color="error"
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            </Box>
                          </Box>
                        ) : (
                          <Box className="text-file-preview">
                            <Box className="file-info">
                              <Typography variant="body2">
                                {selectedFile.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {(selectedFile.size / 1024 / 1024).toFixed(2)}{" "}
                                MB
                              </Typography>
                            </Box>
                            <IconButton
                              onClick={removeFile}
                              color="error"
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    )}
                    {fileError && (
                      <Typography
                        variant="caption"
                        sx={{ color: "error.main", display: "block", mt: 1 }}
                      >
                        {fileError}
                      </Typography>
                    )}
                  </div>
                  {mediaType === "Video" && (
                    <div className="file-upload-group">
                      <InputLabel className="file-label">
                        Upload Thumbnail (Optional)
                      </InputLabel>
                      {!selectedThumbnail ? (
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<CloudUpload />}
                          className="upload-button"
                        >
                          Choose Thumbnail
                          <input
                            type="file"
                            hidden
                            accept="image/png,image/jpg,image/jpeg, image/heif, image/webp"
                            onChange={handleThumbnailUpload}
                          />
                        </Button>
                      ) : (
                        <Box className="thumbnail-preview">
                          <img
                            src={thumbnailPreview || ""}
                            alt="Thumbnail preview"
                            className="thumbnail-image"
                          />
                          <Box className="file-info">
                            <Typography variant="body2">
                              {selectedThumbnail.name}
                            </Typography>
                            <IconButton
                              onClick={removeThumbnail}
                              color="error"
                              size="small"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                      {thumbnailError && (
                        <Typography
                          variant="caption"
                          sx={{ color: "error.main", display: "block", mt: 1 }}
                        >
                          {thumbnailError}
                        </Typography>
                      )}
                    </div>
                  )}
                </div>
                <DialogActions className="dialog-actions-buttons">
                  <Button onClick={onClose} className="cancel-button">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="submit-button"
                    disabled={!isFormValid}
                  >
                    Submit
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddGuidePopup;
